import { QueryClientProvider } from '@tanstack/react-query'
import { useState } from 'react'
import { BrowserRouter, Navigate, Route } from 'react-router-dom'
import { AUTH_PATHS } from './constants/auth'
import {
  PRIVACY_POLICY,
  REFUND_AND_RETURNS_POLICY,
  TERMS_AND_CONDITIONS,
  WEBSITE_DISCLAIMER,
} from './constants/legal-pages'
import { ADDRESS_PATHS } from './constants/addresses'
import { ORDER_PATHS } from './constants/orders'
import { SEARCH_PATHS } from './constants/search'
import { createAppQueryClient } from './helpers/query-client'
import { SentryRoutes } from './helpers/sentry-routes'
import { AccountLayout } from './layout/AccountLayout'
import { MainLayout } from './layout/MainLayout'
import { AboutPage } from './pages/AboutPage'
import { AddressEditPage } from './pages/AddressEditPage'
import { AddressesPage } from './pages/AddressesPage'
import { BlogListPage } from './pages/BlogListPage'
import { BlogPostPage } from './pages/BlogPostPage'
import { ContactPage } from './pages/ContactPage'
import { FaqPage } from './pages/FaqPage'
import { CartPage } from './pages/CartPage'
import { CheckoutPage } from './pages/CheckoutPage'
import { CorporateEnquiryPage } from './pages/CorporateEnquiryPage'
import { HomePage } from './pages/HomePage'
import { LegalPage } from './pages/LegalPage'
import { LocationsPage } from './pages/LocationsPage'
import { LegacyOrderRedirect } from './pages/LegacyOrderRedirect'
import { LoginPage } from './pages/LoginPage'
import { OrderDetailPage } from './pages/OrderDetailPage'
import { OrdersPage } from './pages/OrdersPage'
import { ProductPage } from './pages/ProductPage'
import { ProfilePage } from './pages/ProfilePage'
import { SearchPage } from './pages/SearchPage'
import { ShopPage } from './pages/ShopPage'
import { SignupPage } from './pages/SignupPage'
import { VendorRegistrationPage } from './pages/VendorRegistrationPage'
import { WishlistPage } from './pages/WishlistPage'

function App() {
  const [queryClient] = useState(createAppQueryClient)

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <SentryRoutes>
          <Route element={<MainLayout />}>
            <Route index element={<HomePage />} />
            <Route path="shop/occasions/:slug" element={<ShopPage />} />
            <Route path="shop/occasions" element={<ShopPage />} />
            <Route path="shop/:slug" element={<ShopPage />} />
            <Route path="blog" element={<BlogListPage />} />
            <Route path="blog/:slug" element={<BlogPostPage />} />
            <Route path="product/:slug" element={<ProductPage />} />
            <Route path={SEARCH_PATHS.route} element={<SearchPage />} />
            <Route path="cart" element={<CartPage />} />
            <Route path="checkout" element={<CheckoutPage />} />
            <Route path={AUTH_PATHS.profileRoute} element={<AccountLayout />}>
              <Route index element={<ProfilePage />} />
              <Route path={ADDRESS_PATHS.nestedList} element={<AddressesPage />} />
              <Route path={ADDRESS_PATHS.nestedEdit} element={<AddressEditPage />} />
              <Route path={ORDER_PATHS.nestedList} element={<OrdersPage />} />
              <Route path={ORDER_PATHS.nestedDetail} element={<OrderDetailPage />} />
            </Route>
            <Route path="orders" element={<Navigate to={ORDER_PATHS.list} replace />} />
            <Route path={ORDER_PATHS.legacyDetail} element={<LegacyOrderRedirect />} />
            <Route path="wishlist" element={<WishlistPage />} />
            <Route path="login" element={<LoginPage />} />
            <Route path="signup" element={<SignupPage />} />
            <Route path="register" element={<Navigate to="/signup" replace />} />
            <Route path="vendor-registration" element={<VendorRegistrationPage />} />
            <Route path="corporate-enquiry" element={<CorporateEnquiryPage />} />
            <Route path="about" element={<AboutPage />} />
            <Route path="locations" element={<LocationsPage />} />
            <Route path="contact" element={<ContactPage />} />
            <Route path="faqs" element={<FaqPage />} />
            <Route
              path="return-refund"
              element={<LegalPage content={REFUND_AND_RETURNS_POLICY} />}
            />
            <Route path="privacy" element={<LegalPage content={PRIVACY_POLICY} />} />
            <Route path="terms" element={<LegalPage content={TERMS_AND_CONDITIONS} />} />
            <Route path="disclaimer" element={<LegalPage content={WEBSITE_DISCLAIMER} />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </SentryRoutes>
      </BrowserRouter>
    </QueryClientProvider>
  )
}

export default App
